import React, { useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import useUser from '../useUser';
import {
  GetGiideAnalyticsQuery,
  GetPollQuizResponsesStudioQuery,
} from '../../graphql/api';
import { GiideAnalytics } from '../../graphql/types';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../graphql/types';
import { setAnalyticsData } from '../../actions';

const getPollQuizResponsesStudio = gql`
  query GetPollQuizResponsesStudio($giideID: String!) {
    getPollQuizResponsesStudio(giideId: $giideID) {
      feedCardId
      totalResponses
      pollQuizAnswers {
        index
        percentResponse
        sortOrder
        text
      }
    }
  }
`;

const getGiideAnalyticsQuery = gql`
  query GetGiideAnalytics(
    $giideID: String!
    $grouping: String!
    $period: Int!
    $tenantID: String!
    $userID: String!
    $timezoneOffset: Int!
  ) {
    getGiideAnalytics(
      giideID: $giideID
      grouping: $grouping
      period: $period
      tenantID: $tenantID
      userID: $userID
      timezoneOffset: $timezoneOffset
    ) {
      errorMessage
      details {
        timestamp
        eventName
        count
        feedCardId
      }
      feedCardDetails {
        feedCardIdForAnalytics
        giideId
        iconUrl
        resourceType
        source
        title
        type
        url
        imageUrl
      }
    }
  }
`;

const useAnalytics = (period: number, grouping: string, giideID?: string) => {
  const dispatch = useDispatch();
  const { user } = useUser();

  const [loading, setLoading] = useState<boolean>(false);

  const [pollData, setPollData] =
    useState<GetPollQuizResponsesStudioQuery['getPollQuizResponsesStudio']>();
  const [loadingPollData, setLoadingPollData] = useState<boolean>(false);

  const data = useSelector<IState, GiideAnalytics | undefined>(
    state => state.analyticsData
  );

  const refetch = React.useCallback(() => {
    if (!giideID) return;
    setLoading(true);
    setLoadingPollData(true);
    (
      API.graphql(
        graphqlOperation(getGiideAnalyticsQuery, {
          period,
          grouping,
          giideID: `giide-${giideID}`,
          tenantID: user?.tenantID,
          userID: user?.userId,
          timezoneOffset: new Date().getTimezoneOffset(),
        })
      ) as Promise<{ data: GetGiideAnalyticsQuery }>
    )
      .then(result => {
        const analyticsInput = {
          ...result.data.getGiideAnalytics,
          period,
          grouping,
          id: giideID,
        };
        dispatch(setAnalyticsData(analyticsInput as GiideAnalytics));
        setLoading(false);
      })
      .catch(err => console.log('ANALYTICS ERROR: ', err));
    (
      API.graphql(
        graphqlOperation(getPollQuizResponsesStudio, {
          giideID: `${giideID}`,
        })
      ) as Promise<{ data: GetPollQuizResponsesStudioQuery }>
    )
      .then(result => {
        setPollData(result.data.getPollQuizResponsesStudio);
        setLoadingPollData(false);
      })
      .catch(err => console.log('ANALYTICS ERROR: ', err));
  }, [dispatch, giideID, period, grouping, user?.userId, user?.tenantID]);

  React.useEffect(() => {
    if (
      !data ||
      data.period !== period ||
      data.grouping !== grouping ||
      data.id !== giideID
    ) {
      refetch();
    }
  }, [data, period, grouping, giideID, refetch]);

  return { data, refetch, loading, loadingPollData, pollData };
};

export default useAnalytics;
